import Vue from 'vue';
import moment from 'moment';
import VueValidate, { Validator } from 'vee-validate';
import { IErrorStrings } from "./Models/resourceClasses";
import { getClosestParent } from "./Utilities/objectUtilities";

let dateFormat = "M/D/YYYY";

function isFunctionValid(func: Function): boolean {
    return typeof func === "function";
}

//vee-validate built-in rules: https://baianat.github.io/vee-validate/guide/rules.html
Vue.use(VueValidate, { aria: false, events: 'change' });

//All error messaging lives in this object
const errorStrings: IErrorStrings = {
    "en": {//English
        //Error messages -- KEEP ALPHABETIZED!
        "messages": {//Overwrite the messages that are displayed for the different built-in (or custom) validation types
            "address": (field: string) => `Please review the ${field}.`,
            "alpha": (field: string) => `Please review the ${field}.`,
            "alpha_num": (field: string) => `Please review the ${field}.`,
            "alpha_spaces": (field: string) => `Please review the ${field}.`,
            "age_restriction": (field: string, params: any) => params[0].age=== 17 ? `You must be at least ${(params[0].age + 1)} years or older to sign up for ${( params[0].serviceName )}` : `Parents or guardians of customers aged ${params[0].age} years or younger may sign them up for FRONTIER Miles by calling 1-888-839-4517`,
            "checked": (field: string) => `Please click the checkbox below to accept the ${field}.`,
            "confirmed": (field: string, comparedTo: string) => `The ${comparedTo} and ${field} do not match.`,            
            "date_between": (field: string, params: any) => `The ${field} must be between ${moment(params[0].min).format(dateFormat)} and ${moment(params[0].max).format(dateFormat)}.`,
            "email": (field: string) => `Please review the ${field}`,
            "equals": (field: string, valueToEqual: string) => `${field} does not equal '${valueToEqual}'.`,
            "exclude_dates": (field: string) => `Invalid selection for ${field}.`,
            "existing_email": (field: string) => `The ${field} entered is already in use by a Member.`,
            "existing_member_id": (field: string) => `The ${field} entered was incorrect. Please try again.`,
            "length": (field: string, length: string) => `${field} is not ${length} characters.`,
            "max": (field: string, length: string) => `${field} exceeds the maximum length of ${length} characters.`,
            "max_value": (field: string, value: string) => `${field}'s value cannot be greater than ${value}.`,
            "min": (field: string, length: string) => `${field} is shorter than the minimum length of ${length} characters.`,
            "min_value": (field: string, value: string) => `${field}'s value cannot be less than ${value}.`,
            "member_can_join_pool": (field: string) => `We’re sorry, this member is not eligible to join your pool. The member is already in a pool or recently left a pool.`,
            "not_equal": (field: string, comparedTo: string) => `Please make sure your ${field} and ${comparedTo} are entered correctly.`,
            "numeric": (field: string) => `${field} must be numeric.`,
            "password": (field: string) => `${field} must meet the password requirements.`,
            "phone": (field: string) => `Please review the ${field}.`,
            "required": (field: string) => `Please enter the ${field}.`,
            "tcs_checked": (field: string, message: string) => `You must accept the terms and conditions to ${(message === '' ? field : message)}.`,            
            "zip_code": (field: string) => `Please review the ${field}.`            
        },
        //Field Names -- KEEP ALPHABETIZED!
        "attributes": {//These translate the input's name into a friendly name, ex <input name='confirm_email> will become "Confirmation Address" in the messages above
            "address": "Address",
            "address1": "Address 1",
            "Address 1": "Address 1",
            "address2": "Address 2",
            "billing_email": "Billing Email Address",
            "birth_date": "Date of Birth",
            "card_holder_name": "Cardholder Name",
            "card_month": "Expiration Month",
            "card_number": "Credit Card Number",
            "card_year": "Expiration Year",
            "check_in_date": "Check-in Date",
            "check_out_date": "Check-out Date",
            "city": "City",
            "City": "City",
            "confirm_email": "Confirmation Address",
            "confirm_password": "Password Confirmation",
            "country": "Country",
            "cvc": "Credit Card CVC",
            "depart_date": "Departure Date",
            "depart_time": "Departure Time",
            "email": "Email Address",
            "expiration_month": "Expiration Month",
            "expiration_year": "Expiration Year",
            "family_pooling_conditions": "Family Pooling Conditions",
            "first_name": "First Name",
            "from_station": "Departure City",
            "gender": "Gender",
            "history_filter": "Account History Filter",
            "home_airport_code": "Home Airport Code",
            "inequality_address1": "Address",
            "inequality_Address 1": "Address",
            "inequality_city": "City",
            "inequality_City": "City",
            "ktn": "Known Traveler Number",
            "last_name": "Last Name",
            "member_number": "<i>FRONTIER Miles</i> number",
            "middle_name": "Middle Name",
            "new_email": "Email Address",
            "new_password": "New Password",
            "old_password": "Old Password",
            "passport_expiry_date": "Expiration Date",
            "passport_issue_date": "Issue Date",
            "passport_issuing_country": "Issuing Country",
            "passport_nationality": "Nationality",
            "passport_number": "Passport Number",
            "password": "Password",
            "phone": "Phone Number",
            "pnr": "Confirmation Code",
            "redress": "Redress Number",
            "return_time": "Return Time",
            "return_date": "Return Date",
            "seat_location": "Seat Location",
            "seat_type": "Seat Type",
            "state": "State",
            "to_station": "Destination City",
            "terms_checkbox": "Terms and Conditions",
            "zip_code": "Zip Code"
        }
    }
};

//Add error
Vue.prototype.addError = function (error: string, scroll: boolean = true, field: string = ""): void {
    this.errors.add({ msg: error, field });
    if(scroll) {
        this.scrollToErrors();
    }
}

Vue.prototype.addErrors = function (errors: string[], scroll: boolean = true): void {
    errors.forEach(error => {
        this.addError(error, false);
    });
    if (scroll) {
        this.scrollToErrors();
    }
} 

Vue.prototype.clearErrors = function (): void {
    this.$validator.errors.clear();
    var fields = this.$validator.fields.items;
    var componentFields = this.fields;
    if(fields) {
        fields.forEach(field => {
            field.valid = true;
        });
    }
    if(componentFields) {
        for(var fieldName in componentFields) {
            if(componentFields.hasOwnProperty(fieldName)) {
                var componentField = componentFields[fieldName];
                componentField.valid = true;
                componentField.validated = false;
                componentField.invalid = false;
            }
        }
    }
}

Vue.prototype.scrollToErrors = function (): void {
    this.$nextTick(() => {
        let errorEl = this.$el.querySelector(".error");
        if(errorEl) {
            let parentField = getClosestParent(errorEl, ".slider-field", this.$el);

            if(parentField) {
                let closestField = parentField.querySelector("input,select,textarea");

                if(closestField) {
                    new IntersectionObserver(entries => {
                        let [entry] = entries;
                        if (entry.isIntersecting) {
                            if (isFunctionValid(closestField.focus)) closestField.focus();
                            if (isFunctionValid(closestField.select)) closestField.select();
                        }
                    }).observe(errorEl);
                }
            }
            
            errorEl.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
        }
    });
}

//Validates the current form.
Vue.prototype.validate = function(scope?: string | undefined): Promise<boolean> {
    this.clearErrors();
    this.success = false;
    return this.$validator.validateAll(scope).then((result: boolean) => {
        if(!result) {
            this.scrollToErrors();
        }
        return result;
    });
}

Vue.prototype.validateRuleSuccess = function (field: any, fieldName: string, ruleName: string): boolean {
    var er = this.$validator.errors.items.find((x: {rule: string, field: string}) => x.rule === ruleName && x.field === fieldName);
    if(er) {
        var index = this.$validator.errors.items.indexOf(er);
        this.$validator.errors.items.splice(index, 1);

        if(!this.$validator.errors.items.some((x: {field: string}) => x.field === fieldName)) {
            field.$el.setAttribute('aria-invalid', 'false');
        }

        field.$el.classList.remove('invalid');
    }
    return true;
}

Vue.prototype.validateRuleFail = function (field: any, fieldName: string, errorMessage: string, ruleName: string): boolean {
    this.$validator.errors.add({
        field: fieldName,
        msg: errorMessage,
        rule: ruleName
    }); 

    field.$el.setAttribute('aria-invalid', 'true');
    field.$el.classList.add('invalid');
    this.scrollToErrors();

    return false;
}

//Custom method for comparing two field values. This can go away when vee-validate gets it together with confirmed|required
Vue.prototype.confirmFields = function(ref1Name: string, ref2Name: string): boolean {
    let ref1 = this.$refs[ref1Name], ref2 = this.$refs[ref2Name],
        val1 = ref1.value, val2 = ref2.value;

    if(val1 === val2 || !val1 || !val2) {
        return this.validateRuleSuccess(ref1, ref1Name, "confirmed");
    }

    const name1 = String(errorStrings.en.attributes[ref1Name]) || ref1Name,
        name2 = String(errorStrings.en.attributes[ref2Name]) || ref2Name;
    
    return this.validateRuleFail(ref1, ref1Name, errorStrings.en.messages.confirmed(name1, name2), "confirmed");
}

//Custom method for comparing two field values for inequality. This can go away when vee-validate gets it together with not_equal|required
Vue.prototype.checkInequality = function(ref1Name: string, ref2Name: string): boolean {
    let ref1 = this.$refs[ref1Name], ref2 = this.$refs[ref2Name],
        val1 = ref1.value.trim(), val2 = ref2.value.trim();

    if(val1.toUpperCase() !== val2.toUpperCase() || !val1 || !val2) {
        return this.validateRuleSuccess(ref1, ref1Name, "not_equal");
    }

    const name1 = String(errorStrings.en.attributes["inequality_" + ref1Name]) || ref1Name,
        name2 = String(errorStrings.en.attributes["inequality_" + ref2Name]) || ref2Name;
    
    return this.validateRuleFail(ref1, ref1Name, errorStrings.en.messages.not_equal(name1,name2), "not_equal");
}

//The following are custom valdiation types.  KEEP ALPHABETIZED!
//getMessage: returns the message to display, with optional translated field name -- this makes it more reusable (ex hard coding "First Name" vs. passing in "Passenger 4 First Name")
//validate: apply logic to the field's value and return true or false (or Promise<Boolean> if ajax, etc is necessary)
Validator.extend('address', {
    "validate": (value: string) => {
        //value does not contain special characters not found in addresses.
        return /^[A-z0-9'\.\-\s\,\&]+$/.test(value);
    }
});

Validator.extend('checked', {
    "validate": (value: boolean) => {
        return value;
    }
});

Validator.extend('tcs_checked', {
    "validate": (value: boolean) => {
        return value;
    }
});

Validator.extend('date_between', {
    "validate": (value: Date, params: any) => {
        let d1 = params[0].min, d2 = params[0].max, format = params[1] || dateFormat;

        let min = d1 instanceof Date ? moment(d1) : moment(d1, format), 
            max = d2 instanceof Date ? moment(d2) : moment(d2, format), 
            actual = moment(value);

        return actual.diff(min, "days") * max.diff(actual, "days") >= 0;
    }
});

Validator.extend('equals', {
    "validate": (value: string, valueToEqual: string) => {
        return value == valueToEqual;
    }
});

Validator.extend('exclude_dates', {
    "validate": (value: Date, excludedDates: Array<Date>) => {
        let result = !excludedDates.map(d => d.getTime()).includes(value.getTime());
        return result;
    }
})

Validator.extend('existing_email', {
    "validate": (value: string, host: string) => {
        let url = `${host || ""}/F9/Verify?email=${encodeURIComponent(value)}`;
        
        //email value is not already registered with an existing account
        return fetch(url).then(x => x.json()).then(json => !json.exists).catch(() => false);
    }
});

Validator.extend('existing_member_id', {
    "validate": (value: string) => {
        return fetch(`/F9Sessionless/CheckMemberNumber?memberId=${value}`)
            .then(x => x.ok);
    }
});

Validator.extend('member_can_join_pool', {
    "validate": (value: string) => {
        return fetch(`/F9Sessionless/CheckMemberPoolAvailability?memberId=${value}`)
            .then(x => x.ok);
    }
});

Validator.extend('password', {
    "validate": (value: string) => {
        return /^(?=.*[0-9])(?=.*[\\!@#$%^()*\[\]\{\}\-_+'`=|:;\"<>&\/?])(?=.*[a-z])(?=.*[A-Z])[^~.,_]{8,16}$/.test(value);
    }
});

Validator.extend('phone', {
    "validate": (value: string) => {
        //value matches a valid phone number
        return /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(value);
    }
});

Validator.extend('age_restriction', {
    "validate": (value: Date, params: any) => {
        return moment(value) < moment().subtract((params[0].age + 1), 'years');
    }
});

Validator.extend('zip_code', {
    "validate": (value: string) => {
        //value matches U.S. zip codes.
        return /^\d{5}(?:[-\s]\d{4})?$/.test(value);
    }
});

//This must come last so all of the custom validators and strings dictionary are in place.
Validator.localize(errorStrings);